import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { RouteAnnouncer } from './route-title';
import { ROUTE_TITLES } from './route-titles';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';
import { DetailDrawer } from './DetailDrawer';
import { GlobalStatusBanner } from './GlobalStatusBanner';
import { NewBuildBanner } from './NewBuildBanner';
import { SoundUnlockChip } from './SoundUnlockChip';
import { FireAlarmOverlay } from './FireAlarmOverlay';
import { FireModeHoldNotice } from './FireModeHoldNotice';
import { SiteProvider } from '../state/site';
import { AlertNotificationsProvider } from '../state/alert-notifications';
import { FireAlarmProvider } from '../state/fire-alarm';
import { ToastStack } from '../design-system/components/Toast';
import { ErrorBoundary } from '../design-system/components/ErrorBoundary';
import { useLiveData } from '../api/hooks/useLiveData';
import { useHotkeys } from './use-hotkeys';
import { useScrollTuck } from './use-scroll-tuck';
import { useNavRail } from '../state/nav-rail';
import './AppShell.css';
import { useFireAlarm } from '../state/useFireAlarm';

/**
 * Bridge component that calls `useLiveData` inside the `SiteProvider`
 * boundary. `useLiveData` depends on `useActiveSite` to subscribe to
 * the correct MQTT topics, so it must run as a descendant of the
 * provider. Rendering the hook call from a child component is the
 * cleanest way to keep the provider at the JSX root while still
 * using the hook.
 */
function LiveDataBridge(): null {
  useLiveData();
  return null;
}

/**
 * Global keyboard shortcuts. Mounted under the SiteProvider so any
 * modal/menu/drawer opened in the shell can react to Escape without
 * having to mount its own listener. Sequence style: `g` then a
 * destination key within 1.5 s.
 *
 *   g o → /overview
 *   g f → /fleet
 *   g t → /tasks
 *   g a → /alerts
 *   g d → /devices
 *   g e → /metrics
 *   g x → open the emergency (fire mode) panel
 *   ?   → focus the global search (when implemented)
 */
export function GlobalHotkeys(): null {
  const navigate = useNavigate();
  // Reads the fire context, so this component must stay inside
  // `FireAlarmProvider` -- it does, a few lines below.
  const { openFireModePanel } = useFireAlarm();
  useHotkeys([
    ['g o', () => navigate('/overview')],
    ['g f', () => navigate('/fleet')],
    ['g t', () => navigate('/tasks')],
    ['g a', () => navigate('/alerts')],
    ['g d', () => navigate('/devices')],
    ['g e', () => navigate('/metrics')],
    ['g p', () => navigate('/profile')],
    // NOT a navigation: it opens the emergency panel from anywhere.
    // Added when the flame moved out of the top bar into the avatar
    // menu, so the one control where a lost second matters still has a
    // path that costs no clicks and no hunting. `g f` was already
    // Fleet; `x` is the emergency mnemonic the rest of the industry
    // uses.
    //
    // FOR EVERY ROLE, unlike the menu item (decided 2026-10-31): anyone
    // may check the fire state in an alarm, and the panel shows a role
    // that cannot start or end fire mode the state and the counts with
    // no button it does not hold.
    ['g x', () => openFireModePanel()],
  ]);
  return null;
}

export function AppShell() {
  // Phone-only drawer state. Closed on every route change: a drawer
  // that stays open after you navigate hides the page you just asked
  // for, which reads as "the tap did nothing".
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // The top bar tucks to one slim row while the content is scrolled down
  // (`use-scroll-tuck.ts`): on a phone since option C, on a laptop since
  // the laptop version (both 2026-10-30). This line is the one switch;
  // the CSS keys on `data-bar-tucked` and only decides what gives way at
  // each width. A new page starts with the full bar.
  const shellRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLElement | null>(null);
  const tuck = useScrollTuck(contentRef, shellRef, {
    enabled: true,
    resetKey: location.pathname,
  });
  // Icons-only side menu: the person's choice, remembered per browser
  // (`state/nav-rail.ts`). Drawn from 1024px up (`AppShell.css`).
  const [navRail, setNavRail] = useNavRail();

  return (
    <SiteProvider>
      <AlertNotificationsProvider>
        <FireAlarmProvider>
          <LiveDataBridge />
          <GlobalHotkeys />
          <div
            ref={shellRef}
            className="nx-shell"
            data-nav-open={navOpen ? 'true' : 'false'}
            data-bar-tucked={tuck === 'tucked' ? 'true' : 'false'}
            // Present only once a tucked bar has come back: the reveal
            // animation keys on it, so a page load does not animate.
            data-bar-revealed={tuck === 'restored' ? 'true' : undefined}
            data-nav-rail={navRail ? 'true' : 'false'}
          >
            {/* The scrim is the ::before of .nx-shell, which cannot take
                an onClick -- so this transparent button sits over it and
                carries the dismiss. Rendered only while open so it never
                intercepts anything in the desktop layout. */}
            {navOpen && (
              <button
                type="button"
                aria-label="Close navigation"
                onClick={() => setNavOpen(false)}
                style={{
                  position: 'fixed', inset: 0, zIndex: 56,
                  background: 'transparent', border: 0, cursor: 'default',
                }}
              />
            )}
            <SideNav rail={navRail} onToggleRail={() => setNavRail(!navRail)} />
            <div className="nx-shell__main">
              <TopBar onToggleNav={() => setNavOpen((v) => !v)} navOpen={navOpen} />
              <GlobalStatusBanner />
              {/* A deploy since this page loaded (`new-build.ts`): says so,
                  and waits for Reload. */}
              <NewBuildBanner />
              {/* While the browser holds alert sound silent until a tap
                  (iOS, Chrome before the first click), fire siren
                  included: says so, and a tap turns it on. */}
              <SoundUnlockChip />
              <main className="nx-shell__content" id="main-content" ref={contentRef}>
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              </main>
            </div>
            <DetailDrawer />
            <ToastStack />
            <FireModeHoldNotice />
            <FireAlarmOverlay />
          </div>
          <RouteAnnouncer titles={ROUTE_TITLES} />
        </FireAlarmProvider>
      </AlertNotificationsProvider>
    </SiteProvider>
  );
}